import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Flex, Heading, Text, Button, ButtonGroup, useToast, HStack, SimpleGrid,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Tooltip,
    Alert, AlertIcon, Link as ChakraLink,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaSyncAlt, FaRobot, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getBotUsoIA, getDreIaOrigens } from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const ACCENT = '#F68633';

// Paleta categórica validada para superfície branca (ΔE de daltonismo e de
// visão normal dentro do piso em todos os pares adjacentes da pilha).
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const COR_OUTROS = '#898781';
// 9ª cor (vinho), só da origem saude_anuncios. Validada contra as 8 acima e o
// cinza de "Outros" em qualquer vizinhança da pilha: pior ΔE 12,9 com
// daltonismo (deutan, vs. verde) e 18,3 sem (vs. violeta); contraste 8,1:1.
const COR_EXTRA = '#8f255b';
const SUPERFICIE = '#ffffff';
const TINTA_MUTED = '#898781';
const GRADE = '#e1e0d9';
const COR_ALTA = '#d03b3b';   // custo subiu
const COR_BAIXA = '#006300';  // custo caiu

const PERIODOS = [
    { dias: 7, label: '7 dias' },
    { dias: 15, label: '15 dias' },
    { dias: 30, label: '30 dias' },
    { dias: 90, label: '90 dias' },
];

// Cor fixa por origem (segue a entidade, nunca a posição no ranking — filtrar
// ou trocar de período não repinta as séries). Origens fora do mapa caem em
// "Outros".
const ORIGEM_META = {
    resposta_bot: { label: 'Resposta do bot', cor: SERIES[0] },
    triagem: { label: 'Triagem de conversas', cor: SERIES[1] },
    chat_interno: { label: 'Chat IA interno', cor: SERIES[2] },
    compra_inteligente: { label: 'Compra inteligente', cor: SERIES[3] },
    compatibilidade_catalogo: { label: 'Compatibilidade (catálogo)', cor: SERIES[4] },
    varredura_crm: { label: 'Varredura CRM', cor: SERIES[5] },
    perguntas_ml: { label: 'Perguntas ML', cor: SERIES[6] },
    entrega_solicitacao: { label: 'Entrega de solicitação', cor: SERIES[7] },
    saude_anuncios: { label: 'Saúde dos anúncios (ML)', cor: COR_EXTRA },
    sugestao_atendente: { label: 'Sugestão (atendente)', cor: COR_OUTROS },
    // A paleta tem 8 cores (mais a extra acima) atribuídas em ordem fixa:
    // origem nova entra em "Outros" no gráfico (continua com linha própria na tabela).
    retira_atraso: { label: 'Retirada de atrasos (ML)', cor: COR_OUTROS },
    compatibilidade_chassi_imagem: { label: 'Compatibilidade (chassi por foto)', cor: COR_OUTROS },
    ligacao_voz: { label: 'Ligação (voz)', cor: COR_OUTROS },
    ligacao_backend: { label: 'Ligação (backend)', cor: COR_OUTROS },
    ligacao_resumo: { label: 'Resumo de ligação', cor: COR_OUTROS },
    cotacao_email: { label: 'Cotação por e-mail', cor: COR_OUTROS },
    cotacao_email_triagem: { label: 'Cotação e-mail (triagem)', cor: COR_OUTROS },
    transcricao_audio: { label: 'Transcrição de áudio', cor: COR_OUTROS },
    validacao_imagem: { label: 'Validação de imagens', cor: COR_OUTROS },
    estimador_dimensoes: { label: 'Estimador de dimensões', cor: COR_OUTROS },
    correcao_anuncio_ml: { label: 'Correção de anúncio (ML)', cor: COR_OUTROS },
    categoria_ml: { label: 'Categoria ML (Jev)', cor: COR_OUTROS },
    veiculos_compativeis: { label: 'Veículos compatíveis (Jev)', cor: COR_OUTROS },
    filtro_malicioso: { label: 'Filtro de mensagem maliciosa (Jev)', cor: COR_OUTROS },
    avaliacao_atendimento: { label: 'Avaliação de atendimento', cor: COR_OUTROS },
    devolucao_autorizacao_pdf: { label: 'Autorização de devolução (PDF)', cor: COR_OUTROS },
    desenho_catalogo_ia: { label: 'Desenho do catálogo (IA)', cor: COR_OUTROS },
    desenho_catalogo_conferencia: { label: 'Conferência do desenho (IA)', cor: COR_OUTROS },
};

const TIPO_META = [
    { chave: 'input', label: 'Entrada', cor: SERIES[0], nota: 'Prompt enviado sem cache' },
    { chave: 'output', label: 'Saída', cor: SERIES[1], nota: 'Texto gerado — 5x o preço da entrada' },
    { chave: 'cache_read', label: 'Cache lido', cor: SERIES[2], nota: 'Prompt reaproveitado — 10% do preço' },
    { chave: 'cache_write', label: 'Cache gravado', cor: SERIES[3], nota: 'Gravação do prompt no cache — 125% do preço' },
];

const DIMENSOES = [
    { id: 'origem', label: 'Origem' },
    { id: 'tipo', label: 'Tipo de token' },
    { id: 'modelo', label: 'Modelo' },
];

const fmtInt = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtUsd = (v, casas = 2) =>
    'US$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
const fmtPct = (v) => `${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const fmtDia = (iso) => {
    const [, m, d] = String(iso).split('-');
    return `${d}/${m}`;
};
const labelOrigem = (o) => ORIGEM_META[o]?.label || o;
const corOrigem = (o) => ORIGEM_META[o]?.cor || COR_OUTROS;

/** Variação percentual entre dois períodos. null quando não há base de comparação. */
const variacao = (atual, anterior) => {
    const a = Number(anterior || 0);
    if (a <= 0) return null;
    return ((Number(atual || 0) - a) / a) * 100;
};

/** Seta + percentual. Para custo, subir é ruim — daí a cor, sempre com a seta junto. */
const Delta = ({ pct, sufixo = 'vs. período anterior' }) => {
    if (pct === null || !isFinite(pct)) return null;
    const subiu = pct >= 0;
    const Icone = subiu ? FaArrowUp : FaArrowDown;
    return (
        <HStack spacing={1} mt={1} color={subiu ? COR_ALTA : COR_BAIXA}>
            <Icone size={9} />
            <Text fontSize="xs" fontWeight="semibold">
                {fmtPct(Math.abs(pct))}
            </Text>
            <Text fontSize="xs" color="gray.400" fontWeight="normal">{sufixo}</Text>
        </HStack>
    );
};

const StatCard = ({ label, value, hint, pct }) => (
    <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}>
        <Text fontSize="sm" color="gray.500">{label}</Text>
        <Text fontSize="2xl" fontWeight="bold" color="gray.800" lineHeight="1.2" mt={1}>{value}</Text>
        {pct !== undefined ? <Delta pct={pct} /> : null}
        {hint && <Text fontSize="xs" color="gray.400" mt={1}>{hint}</Text>}
    </Box>
);

const Card = ({ titulo, extra, children, ...rest }) => (
    <Box bg="white" borderWidth="1px" borderRadius="lg" p={4} {...rest}>
        <Flex align="baseline" justify="space-between" mb={3} wrap="wrap" gap={2}>
            <Text fontWeight="semibold">{titulo}</Text>
            {extra}
        </Flex>
        {children}
    </Box>
);

/** Barrinha de participação dentro da tabela — o número continua na célula. */
const BarraShare = ({ pct, cor }) => (
    <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden" minW="60px">
        <Box h="100%" w={`${Math.max(0, Math.min(100, pct))}%`} bg={cor} borderRadius="full" />
    </Box>
);

/** Há algum segmento com valor acima deste na mesma barra? */
const temSegmentoAcima = (ctx) => {
    const { datasets } = ctx.chart.data;
    if (!Number(datasets[ctx.datasetIndex].data[ctx.dataIndex])) return false;
    return datasets.slice(ctx.datasetIndex + 1).some((d) => Number(d.data[ctx.dataIndex]) > 0);
};

/**
 * Monta os datasets empilhados a partir de uma série diária
 * ([{ dia, <fatia>: custo }]) usando uma lista fixa de fatias.
 */
const montarDatasets = (serie, fatias) =>
    fatias.map(({ chave, label, cor }) => ({
        label,
        backgroundColor: cor,
        data: serie.map((linha) => Number(linha[chave] || 0)),
        // 2px da própria superfície separam um segmento do de cima — no topo da
        // pilha não há vizinho, então a barra não perde altura à toa.
        borderColor: SUPERFICIE,
        borderSkipped: false,
        borderWidth: (ctx) => ({ top: temSegmentoAcima(ctx) ? 2 : 0, right: 0, bottom: 0, left: 0 }),
        // Só a ponta da pilha arredonda; o resto encosta reto no vizinho.
        borderRadius: (ctx) => (temSegmentoAcima(ctx) ? 0 : 4),
    }));

/** Soma o custo de cada fatia no período inteiro (para ordenar/dobrar em "Outros"). */
const somarFatias = (serie) => {
    const soma = {};
    serie.forEach((linha) => {
        Object.entries(linha).forEach(([k, v]) => {
            if (k !== 'dia') soma[k] = (soma[k] || 0) + Number(v || 0);
        });
    });
    return soma;
};

const BotCustosIA = () => {
    const toast = useToast();
    const [dias, setDias] = useState(7);
    const [dimensao, setDimensao] = useState('origem');
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    // Origens sem classificação contábil no DRE (#2384).
    const [iaPendentes, setIaPendentes] = useState([]);

    const carregar = useCallback(async (d) => {
        setLoading(true);
        try {
            const { data } = await getBotUsoIA({ dias: d, top: 30 });
            setDados(data);
        } catch (e) {
            toast({ title: 'Erro ao carregar os custos de IA.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { carregar(dias); }, [carregar, dias]);

    // Pendências de classificação são independentes do período escolhido: uma
    // consulta só, na montagem da tela.
    useEffect(() => {
        let ativo = true;
        getDreIaOrigens().then((res) => {
            if (ativo && res && res.pendentes) setIaPendentes(res.pendentes);
        });
        return () => { ativo = false; };
    }, []);

    const totais = dados?.totais || {};
    const anterior = dados?.totais_anterior || {};
    const custoPorTipo = dados?.custo_por_tipo || {};
    const porOrigem = useMemo(() => dados?.por_origem || [], [dados]);
    const porModelo = dados?.por_modelo || [];
    const porDia = useMemo(() => dados?.por_dia || [], [dados]);
    const topContatos = dados?.top_contatos || [];

    const custoTotal = Number(totais.custo_usd || 0);

    // Média diária ignora o dia de hoje, que ainda está correndo e puxaria a
    // média (e a projeção) para baixo.
    const { mediaDia, diasFechados } = useMemo(() => {
        const hoje = dados?.ate;
        const fechados = porDia.filter((d) => String(d.dia) !== hoje);
        const base = fechados.length ? fechados : porDia;
        const soma = base.reduce((s, d) => s + Number(d.custo_usd || 0), 0);
        return { mediaDia: base.length ? soma / base.length : 0, diasFechados: fechados.length };
    }, [porDia, dados]);

    // Fatias do gráfico conforme a dimensão escolhida. Cada fatia tem cor fixa;
    // o que passa de 8 séries (ou não está mapeado) vira "Outros".
    const { serie, fatias } = useMemo(() => {
        if (dimensao === 'tipo') {
            return {
                serie: dados?.por_dia_tipo || [],
                fatias: TIPO_META.map(({ chave, label, cor }) => ({ chave, label, cor })),
            };
        }

        if (dimensao === 'modelo') {
            const s = dados?.por_dia_modelo || [];
            // Os 8 que mais custaram no período ganham cor (modelo caro nunca
            // cai em "Outros"); entre eles, ordem alfabética, para a cor não
            // pular quando dois trocam de posição no ranking.
            const soma = somarFatias(s);
            const porCusto = Object.keys(soma).sort((a, b) => soma[b] - soma[a] || a.localeCompare(b));
            const principais = porCusto.slice(0, SERIES.length).sort();
            const resto = porCusto.slice(SERIES.length);
            const f = principais.map((chave, i) => ({ chave, label: chave, cor: SERIES[i] }));
            if (resto.length) {
                f.push({ chave: '__outros__', label: `Outros (${resto.length})`, cor: COR_OUTROS });
            }
            const serieDobrada = resto.length
                ? s.map((l) => ({ ...l, __outros__: resto.reduce((t, k) => t + Number(l[k] || 0), 0) }))
                : s;
            return { serie: serieDobrada, fatias: f };
        }

        const s = dados?.por_dia_origem || [];
        const presentes = Object.keys(somarFatias(s));
        const ordem = Object.keys(ORIGEM_META).filter((k) => presentes.includes(k) && ORIGEM_META[k].cor !== COR_OUTROS);
        const restantes = presentes.filter((k) => !ordem.includes(k));
        const f = ordem.map((chave) => ({ chave, label: labelOrigem(chave), cor: corOrigem(chave) }));
        if (restantes.length) {
            f.push({ chave: '__outros__', label: `Outros (${restantes.length})`, cor: COR_OUTROS });
        }
        const serieDobrada = restantes.length
            ? s.map((l) => ({ ...l, __outros__: restantes.reduce((t, k) => t + Number(l[k] || 0), 0) }))
            : s;
        return { serie: serieDobrada, fatias: f };
    }, [dados, dimensao]);

    const chamadasPorDia = useMemo(() => {
        const m = {};
        porDia.forEach((d) => { m[d.dia] = Number(d.chamadas || 0); });
        return m;
    }, [porDia]);

    const chartData = useMemo(() => ({
        labels: serie.map((l) => fmtDia(l.dia)),
        datasets: montarDatasets(serie, fatias),
    }), [serie, fatias]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                // Série única se identifica pelo título do card.
                display: fatias.length > 1,
                position: 'top',
                align: 'start',
                labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 14 },
            },
            tooltip: {
                itemSort: (a, b) => b.parsed.y - a.parsed.y,
                filter: (item) => item.parsed.y > 0,
                callbacks: {
                    label: (ctx) => {
                        const total = ctx.chart.data.datasets
                            .reduce((s, d) => s + Number(d.data[ctx.dataIndex] || 0), 0);
                        const pct = total ? (ctx.parsed.y / total) * 100 : 0;
                        return ` ${ctx.dataset.label}: ${fmtUsd(ctx.parsed.y)} (${fmtPct(pct)})`;
                    },
                    footer: (itens) => {
                        if (!itens.length) return '';
                        const i = itens[0].dataIndex;
                        const total = itens[0].chart.data.datasets
                            .reduce((s, d) => s + Number(d.data[i] || 0), 0);
                        const chamadas = chamadasPorDia[serie[i]?.dia] || 0;
                        return `Total do dia: ${fmtUsd(total)} · ${fmtInt(chamadas)} chamadas`;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: { display: false },
                border: { color: GRADE },
                ticks: { color: TINTA_MUTED, autoSkip: true, maxRotation: 0, maxTicksLimit: 16 },
            },
            y: {
                stacked: true,
                beginAtZero: true,
                grid: { color: GRADE, drawTicks: false },
                border: { display: false },
                ticks: { color: TINTA_MUTED, padding: 8, callback: (v) => `US$ ${v}` },
            },
        },
    }), [chamadasPorDia, serie, fatias]);

    const totalTipos = TIPO_META.reduce((s, t) => s + Number(custoPorTipo[t.chave] || 0), 0);

    return (
        <Box p={{ base: 4, md: 6 }} maxW="1280px" mx="auto">
            <Flex align="center" justify="space-between" mb={5} wrap="wrap" gap={3}>
                <HStack spacing={3}>
                    <FaRobot size={22} color={ACCENT} />
                    <Box>
                        <Heading size="md">Custos de IA</Heading>
                        <Text fontSize="sm" color="gray.500">
                            Onde o gasto com IA está crescendo: por origem, por tipo de token e por modelo.
                        </Text>
                    </Box>
                </HStack>
                <HStack>
                    {PERIODOS.map((p) => (
                        <Button
                            key={p.dias}
                            size="sm"
                            variant={dias === p.dias ? 'solid' : 'outline'}
                            bg={dias === p.dias ? ACCENT : undefined}
                            color={dias === p.dias ? 'white' : undefined}
                            _hover={dias === p.dias ? { bg: ACCENT } : undefined}
                            onClick={() => setDias(p.dias)}
                        >
                            {p.label}
                        </Button>
                    ))}
                    <Button size="sm" leftIcon={<FaSyncAlt />} onClick={() => carregar(dias)} isLoading={loading}>
                        Atualizar
                    </Button>
                </HStack>
            </Flex>

            {/* Origens que o DRE ainda não sabe classificar (#2384). O custo já
                entra no DRE em "Tokens IA > Outros", então isto é um lembrete de
                classificação, não um erro. */}
            {iaPendentes.length > 0 && (
                <Alert status="warning" borderRadius="md" mb={5} fontSize="sm">
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="semibold">
                            {iaPendentes.length === 1
                                ? '1 origem ainda não tem classificação contábil no DRE'
                                : `${iaPendentes.length} origens ainda não têm classificação contábil no DRE`}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                            {iaPendentes.map(o => o.rotulo || o.origem).join(', ')}: o custo está sendo
                            lançado em <b>Tokens IA &gt; Outros</b>. Defina a classificação no bloco
                            Custos de IA em{' '}
                            <ChakraLink as={RouterLink} to="/dre/custos" color="blue.600" fontWeight="medium">
                                DRE &gt; Custos
                            </ChakraLink>.
                        </Text>
                    </Box>
                </Alert>
            )}

            {loading && !dados ? (
                <Flex justify="center" py={20}><Spinner size="lg" color={ACCENT} /></Flex>
            ) : (
                // Em recarga, segura o render anterior esmaecido — sem piscar esqueleto.
                <Box opacity={loading ? 0.55 : 1} transition="opacity .15s">
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
                        <StatCard
                            label="Custo no período"
                            value={fmtUsd(custoTotal)}
                            pct={variacao(custoTotal, anterior.custo_usd)}
                            hint={`${dados?.desde || '-'} até hoje`}
                        />
                        <StatCard
                            label="Média por dia"
                            value={fmtUsd(mediaDia)}
                            hint={`≈ ${fmtUsd(mediaDia * 30)}/mês · ${fmtInt(diasFechados)} dias fechados (hoje não conta)`}
                        />
                        <StatCard
                            label="Chamadas à IA"
                            value={fmtInt(totais.chamadas)}
                            pct={variacao(totais.chamadas, anterior.chamadas)}
                        />
                        <StatCard
                            label="Custo por chamada"
                            value={fmtUsd(totais.chamadas ? custoTotal / totais.chamadas : 0, 4)}
                            pct={variacao(
                                totais.chamadas ? custoTotal / totais.chamadas : 0,
                                anterior.chamadas ? Number(anterior.custo_usd || 0) / anterior.chamadas : 0,
                            )}
                        />
                    </SimpleGrid>

                    <Card
                        titulo="Custo por dia"
                        mb={6}
                        extra={
                            <ButtonGroup size="xs" isAttached variant="outline">
                                {DIMENSOES.map((d) => (
                                    <Button
                                        key={d.id}
                                        onClick={() => setDimensao(d.id)}
                                        bg={dimensao === d.id ? ACCENT : 'white'}
                                        color={dimensao === d.id ? 'white' : 'gray.600'}
                                        _hover={dimensao === d.id ? { bg: ACCENT } : { bg: 'gray.50' }}
                                    >
                                        {d.label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        }
                    >
                        {serie.length === 0 ? (
                            <Text color="gray.500" py={8} textAlign="center">Nenhum uso registrado no período.</Text>
                        ) : (
                            // Altura folgada: legenda e rótulos do eixo moram
                            // dentro do canvas e não podem espremer o gráfico.
                            <Box h={{ base: '300px', md: '380px' }}>
                                <Bar data={chartData} options={chartOptions} />
                            </Box>
                        )}
                    </Card>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={6}>
                        <Card
                            titulo="Para onde vai o dinheiro"
                            extra={<Text fontSize="xs" color="gray.400">por tipo de token</Text>}
                        >
                            <Flex h="10px" borderRadius="full" overflow="hidden" gap="2px" mb={4}>
                                {TIPO_META.map((t) => {
                                    const v = Number(custoPorTipo[t.chave] || 0);
                                    const pct = totalTipos ? (v / totalTipos) * 100 : 0;
                                    return pct > 0 ? <Box key={t.chave} bg={t.cor} flex={`0 0 ${pct}%`} /> : null;
                                })}
                            </Flex>
                            <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {TIPO_META.map((t) => {
                                    const v = Number(custoPorTipo[t.chave] || 0);
                                    const pct = totalTipos ? (v / totalTipos) * 100 : 0;
                                    return (
                                        <Flex key={t.chave} align="center" gap={3} py={2} borderTopWidth="1px" borderColor="gray.100">
                                            <Box w="10px" h="10px" borderRadius="full" bg={t.cor} flexShrink={0} />
                                            <Box flex="1" minW={0}>
                                                <Text fontSize="sm" fontWeight="medium">{t.label}</Text>
                                                <Text fontSize="xs" color="gray.400">{t.nota}</Text>
                                            </Box>
                                            <Text fontSize="sm" color="gray.500" w="52px" textAlign="right">{fmtPct(pct)}</Text>
                                            <Text fontSize="sm" fontWeight="semibold" w="96px" textAlign="right">{fmtUsd(v)}</Text>
                                        </Flex>
                                    );
                                })}
                            </Box>
                        </Card>

                        <Card titulo="Por modelo">
                            <TableContainer>
                                <Table size="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    <Thead>
                                        <Tr>
                                            <Th>Modelo</Th>
                                            <Th isNumeric>Chamadas</Th>
                                            <Th isNumeric>Custo/chamada</Th>
                                            <Th isNumeric>Custo</Th>
                                            <Th isNumeric>%</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {porModelo.map((m) => (
                                            <Tr key={m.modelo}>
                                                <Td>{m.modelo}</Td>
                                                <Td isNumeric>{fmtInt(m.chamadas)}</Td>
                                                <Td isNumeric>{fmtUsd(m.custo_medio, 4)}</Td>
                                                <Td isNumeric fontWeight="semibold">{fmtUsd(m.custo_usd)}</Td>
                                                <Td isNumeric color="gray.500">
                                                    {fmtPct(custoTotal ? (m.custo_usd / custoTotal) * 100 : 0)}
                                                </Td>
                                            </Tr>
                                        ))}
                                        {porModelo.length === 0 && (
                                            <Tr><Td colSpan={5}><Text color="gray.500">Sem dados no período.</Text></Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </SimpleGrid>

                    <Card
                        titulo="Por origem"
                        mb={6}
                        extra={<Text fontSize="xs" color="gray.400">variação comparada aos {dias} dias anteriores</Text>}
                    >
                        <TableContainer>
                            <Table size="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                <Thead>
                                    <Tr>
                                        <Th>Origem</Th>
                                        <Th isNumeric>Chamadas</Th>
                                        <Th isNumeric>Iterações</Th>
                                        <Th isNumeric>Custo/chamada</Th>
                                        <Th isNumeric>Custo</Th>
                                        <Th>Participação</Th>
                                        <Th isNumeric>Variação</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {porOrigem.map((r) => {
                                        const pct = custoTotal ? (r.custo_usd / custoTotal) * 100 : 0;
                                        const v = variacao(r.custo_usd, r.custo_usd_anterior);
                                        return (
                                            <Tr key={r.origem}>
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <Box w="8px" h="8px" borderRadius="full" bg={corOrigem(r.origem)} flexShrink={0} />
                                                        <Text>{labelOrigem(r.origem)}</Text>
                                                    </HStack>
                                                </Td>
                                                <Td isNumeric>{fmtInt(r.chamadas)}</Td>
                                                <Td isNumeric color="gray.500">
                                                    {Number(r.iteracoes_medias || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
                                                </Td>
                                                <Td isNumeric>{fmtUsd(r.custo_medio, 4)}</Td>
                                                <Td isNumeric fontWeight="semibold">{fmtUsd(r.custo_usd)}</Td>
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <BarraShare pct={pct} cor={corOrigem(r.origem)} />
                                                        <Text fontSize="xs" color="gray.500" w="44px" textAlign="right">{fmtPct(pct)}</Text>
                                                    </HStack>
                                                </Td>
                                                <Td isNumeric>
                                                    {v === null ? (
                                                        <Text fontSize="xs" color="gray.400">novo</Text>
                                                    ) : (
                                                        <Text fontSize="xs" fontWeight="semibold" color={v >= 0 ? COR_ALTA : COR_BAIXA}>
                                                            {v >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(v))}
                                                        </Text>
                                                    )}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                    {porOrigem.length === 0 && (
                                        <Tr><Td colSpan={7}><Text color="gray.500">Nenhum uso registrado no período.</Text></Td></Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </Card>

                    <Card
                        titulo="Contatos que mais gastaram"
                        extra={
                            <Text fontSize="xs" color="gray.400">
                                Soma das chamadas ligadas ao contato (resposta, triagem, varredura, entrega, sugestão)
                            </Text>
                        }
                    >
                        <TableContainer>
                            <Table size="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                <Thead>
                                    <Tr>
                                        <Th>#</Th>
                                        <Th>Contato</Th>
                                        <Th>Telefone</Th>
                                        <Th isNumeric>Chamadas</Th>
                                        <Th isNumeric>Custo/chamada</Th>
                                        <Th isNumeric>Custo</Th>
                                        <Th isNumeric>% do total</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {topContatos.map((c, i) => (
                                        <Tr key={c.poli_contact_id}>
                                            <Td color="gray.400">{i + 1}</Td>
                                            <Td>
                                                <Tooltip label={`Poli contact_id ${c.poli_contact_id}`}>
                                                    <Text noOfLines={1} maxW="220px">{c.nome || `Contato ${c.poli_contact_id}`}</Text>
                                                </Tooltip>
                                            </Td>
                                            <Td>{c.telefone || '-'}</Td>
                                            <Td isNumeric>{fmtInt(c.chamadas)}</Td>
                                            <Td isNumeric>{fmtUsd(c.chamadas ? c.custo_usd / c.chamadas : 0, 4)}</Td>
                                            <Td isNumeric fontWeight="semibold">{fmtUsd(c.custo_usd)}</Td>
                                            <Td isNumeric color="gray.500">
                                                {fmtPct(custoTotal ? (c.custo_usd / custoTotal) * 100 : 0)}
                                            </Td>
                                        </Tr>
                                    ))}
                                    {topContatos.length === 0 && (
                                        <Tr><Td colSpan={7}><Text color="gray.500">Nenhum contato com uso no período.</Text></Td></Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Box>
            )}
        </Box>
    );
};

export default BotCustosIA;
